import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Meter } from 'grommet';
import { FormClose, StatusGood } from 'grommet-icons';
import { v4 as uuidv4 } from 'uuid';
import Papa from 'papaparse';
import { useAppTheme } from '@Components/Context';
import { AppButton } from '@Components/Control';
import { validateCsvRows, baseServerUrl } from '@Helpers/util';
import {
  StyledUploadContainer, StyledUploadInput, StyledFormInputClear,
} from '@Components/Partial/Product/NewSearch/StyledProductFormComponent.jsx';
import { paths } from '@Components/configs';
import { MAX_ASSETS_PER_UPLOAD, ASSET_UPLOAD_CHUNK_SIZE } from '@Components/Partial/Product/ContentId/constants';

const BulkAssetUploader = ({
  csvHeaders,
  uploadBulkAssets,
  onS3UploadStart,
  assetLabel,
  companyId,
  withCsv,
  paymentPending,
  isRedflagUpload = false,
  hasAllowlistedChannels = false,
}) => {
  const [files, setFiles] = React.useState([]);
  const [csvFile, setCsvFile] = React.useState(null);
  const [csvError, setCsvError] = React.useState('');
  const [generalError, setGeneralError] = React.useState('');
  const [progress, setProgress] = React.useState({});
  const [status, setStatus] = React.useState({});
  const [uploading, setUploading] = React.useState(false);
  const [filesUploaded, setFilesUploaded] = React.useState(false);
  const [youtubeUploading, setYoutubeUploading] = React.useState(false);
  const [youtubeSuccess, setYoutubeSuccess] = React.useState(false);
  const [requestUuid] = React.useState(uuidv4());
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  // File input handlers
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).filter((f) => f.type !== 'text/csv');
    // Merge with existing files, avoiding duplicates by name
    const mergedFiles = [
      ...files,
      ...newFiles.filter(
        (nf) => !files.some((ef) => ef.name === nf.name),
      ),
    ].slice(0, MAX_ASSETS_PER_UPLOAD);

    if (mergedFiles.length > MAX_ASSETS_PER_UPLOAD) {
      setGeneralError(`You can only upload up to ${MAX_ASSETS_PER_UPLOAD} files at once.`);
      return;
    }

    setFiles(mergedFiles);
    setProgress({});
    // Set status to "Ready" for each selected file
    const readyStatus = {};
    mergedFiles.forEach((f) => { readyStatus[f.name] = 'Ready'; });
    setStatus(readyStatus);

    setGeneralError('');
    setFilesUploaded(false);
    setYoutubeSuccess(false);
  };

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    setCsvError('');
    setGeneralError('');
    setFilesUploaded(false);
    setYoutubeSuccess(false);

    // Set status to "Ready" for CSV file
    if (file) {
      setStatus((prev) => ({ ...prev, [file.name]: 'Ready' }));
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            if (!results.data || results.data.length === 0) {
              setCsvError(['CSV file must contain at least one row of data.']);
              return;
            }
            const validation = validateCsvRows(results.data, csvHeaders);
            if (!validation.valid) {
              setCsvError(
                Array.isArray(validation.errors) ? validation.errors : [validation.error],
              );
            } else {
              setCsvError('');
            }
          } catch (err) {
            setCsvError([err.message || 'Error validating CSV file.']);
          }
        },
        error: () => setCsvError(['Invalid CSV format.']),
      });
    }
  };

  // S3 upload logic
  const uploadFile = async (file) => {
    try {
      setStatus((prev) => ({ ...prev, [file.name]: 'Processing...' }));
      const initRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'init',
          fileName: file.name,
          contentType: file.type,
          requestId: requestUuid,
          companyId,
        }),
      });
      if (!initRes.ok) throw new Error();
      const { uploadId } = await initRes.json();

      const partCount = Math.ceil(file.size / ASSET_UPLOAD_CHUNK_SIZE);
      const etags = [];

      const uploadPartFns = Array.from({ length: partCount }, (_, idx) => async () => {
        const partNumber = idx + 1;
        const start = idx * ASSET_UPLOAD_CHUNK_SIZE;
        const end = Math.min(start + ASSET_UPLOAD_CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);

        const urlRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'part',
            fileName: file.name,
            requestId: requestUuid,
            uploadId,
            partNumber,
            companyId,
          }),
        });
        if (!urlRes.ok) throw new Error();
        const { url } = await urlRes.json();

        const uploadRes = await fetch(url, {
          method: 'PUT',
          body: blob,
        });
        if (!uploadRes.ok) throw new Error();
        const etag = uploadRes.headers.get('etag') || uploadRes.headers.get('ETag');
        etags.push({ ETag: etag.replace(/"/g, ''), PartNumber: partNumber });

        setProgress((prev) => ({
          ...prev,
          [file.name]: Math.round(((partNumber) / partCount) * 100),
        }));
      });

      await uploadPartFns.reduce((p, fn) => p.then(fn), Promise.resolve());

      setStatus((prev) => ({ ...prev, [file.name]: 'Completing...' }));
      const completeRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'complete',
          fileName: file.name,
          requestId: requestUuid,
          uploadId,
          parts: etags,
          companyId,
        }),
      });
      if (!completeRes.ok) throw new Error();
      setStatus((prev) => ({ ...prev, [file.name]: 'Done' }));
    } catch (err) {
      setGeneralError('An error occurred during upload. Please try again.');
      setUploading(false);
      throw err;
    }
  };

  /* eslint-disable camelcase */
  const handleBeginUpload = async () => {
    if (paymentPending) return;
    setGeneralError('');
    if (withCsv && csvFile && csvError) {
      setGeneralError('Please fix the CSV errors before uploading.');
      return;
    }

    onS3UploadStart();
    setUploading(true);
    setYoutubeUploading(false);
    setYoutubeSuccess(false);

    try {
      // Step 1: Upload all files to S3
      const allFiles = withCsv && csvFile ? [...files, csvFile] : files;
      await Promise.all(allFiles.map(uploadFile));
      setUploading(false);
      setFilesUploaded(true);

      // Step 2: Call YouTube/bulk asset API
      setYoutubeUploading(true);
      const response = await uploadBulkAssets({
        request_id: requestUuid,
        asset_label: assetLabel,
      });
      setYoutubeUploading(false);

      if (
        response && response.status === 'pending'
        && (response.request_id === requestUuid || response.requestId === requestUuid)
      ) {
        setYoutubeSuccess(true);
      } else {
        setGeneralError('Failed to start YouTube upload. Please try again.');
      }
    } catch (err) {
      setUploading(false);
      setYoutubeUploading(false);
      setFilesUploaded(false);
      setGeneralError('An error occurred during upload. Please try again.');
    }
  };
  /* eslint-enable camelcase */

  if (generalError) {
    return (
      <Box pad="1rem" background="#FCF8EF" round="0.35rem" align="center" gap="1rem">
        <Text color="var(--app-error)" size="1.1rem" weight={600}>
          {generalError}
        </Text>
        <AppButton
          overrideHover
          level="dynamicLarge"
          width="14rem"
          color={button.primaryText}
          bgColor={button.primaryBg}
          fontWeight={600}
          label="Refresh"
          onClick={() => window.location.reload()}
        />
      </Box>
    );
  }

  const allFiles = [...files, ...(withCsv && csvFile ? [csvFile] : [])].filter(Boolean);
  const uploadDisabled = paymentPending
    || !assetLabel
    || uploading
    || youtubeUploading
    || files.length === 0
    || (withCsv && csvFile && csvError)
    || youtubeSuccess
    || isRedflagUpload
    || !hasAllowlistedChannels;
  const chooseFileDisabled = uploading
    || filesUploaded
    || paymentPending
    || isRedflagUpload
    || !hasAllowlistedChannels;

  return (
    <Box width="100%">
      <Box gap="1rem">
        <Text size="1rem" weight={600} color={page.primaryText}>
          Asset Files
          {' '}
          <Text size="0.9rem" color={page.hintText}>
            {`(required, up to ${MAX_ASSETS_PER_UPLOAD} files)`}
          </Text>
        </Text>
        <StyledUploadContainer highlight={page.focusHighlight}>
          <Box fill gap="1rem" align="center" direction="row">
            <Text size="0.875rem" color={page.primaryText}>
              {files.length > 0
                ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
                : 'No files selected'}
            </Text>
            <AppButton
              level="xsmall"
              width="fit-content"
              label="Choose Asset Files..."
              color={page.altHighlight}
              disabled={chooseFileDisabled}
              style={{ whiteSpace: 'nowrap', paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
            />
          </Box>
          <StyledUploadInput
            disabled={chooseFileDisabled}
            type="file"
            multiple
            accept="video/*"
            onChange={handleFileChange}
            highlight={page.focusHighlight}
          />
        </StyledUploadContainer>
      </Box>
      {withCsv && (
        <Box gap="0.5rem" margin={{ top: '1rem' }}>
          <Text size="1rem" weight={600} color={page.primaryText}>
            CSV Metadata File
            {' '}
            <Text size="0.9rem" color={page.hintText}>(optional)</Text>
          </Text>
          <StyledUploadContainer highlight={page.focusHighlight}>
            <Box fill gap="1rem" align="center" direction="row">
              <Text size="0.875rem" color={page.primaryText}>
                {csvFile ? csvFile.name : 'No CSV selected'}
              </Text>
              <AppButton
                level="xsmall"
                label="Choose CSV..."
                width="fit-content"
                color={page.altHighlight}
                disabled={uploading || filesUploaded}
                style={{ whiteSpace: 'nowrap', paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
              />
            </Box>
            <StyledUploadInput
              disabled={uploading || filesUploaded}
              type="file"
              accept=".csv"
              onChange={handleCsvChange}
              highlight={page.focusHighlight}
            />
          </StyledUploadContainer>
        </Box>
      )}
      <Box
        direction="row"
        gap="1rem"
        margin={{ top: '1.5rem' }}
      >
        <AppButton
          overrideHover
          disabled={uploadDisabled}
          level="dynamicLarge"
          width="14rem"
          color={button.primaryText}
          bgColor={button.primaryBg}
          fontWeight={600}
          label={(uploading || youtubeUploading) ? 'Uploading Assets...' : 'Begin Asset Upload'}
          onClick={handleBeginUpload}
        />
      </Box>
      {csvError && Array.isArray(csvError) && (
        <Box margin={{ top: '1rem' }}>
          <Box direction="column" margin={{ bottom: '0.5rem' }}>
            <Text color="var(--app-error)" size="1rem">Please fix the following errors and re-upload the CSV file:</Text>
          </Box>
          {csvError.map((err) => (
            <Text key={err} color="var(--app-error)" size="1rem">{err}</Text>
          ))}
        </Box>
      )}
      {youtubeSuccess && (
        <Box
          pad="1rem"
          margin={{ top: '1.5rem' }}
          background={isDarkMode ? page.tableBg : page.altBg}
          round="var(--app-radius)"
          align="center"
          gap="1rem"
        >
          <Text color="var(--app-success)" size="1.1rem" weight={600}>
            YouTube upload started! You can track progress in the details view.
          </Text>
          <AppButton
            overrideHover
            level="dynamicLarge"
            width="14rem"
            color={button.primaryText}
            bgColor={button.primaryBg}
            href={paths.productAssetUploadDetails.replace(':id', requestUuid)}
            fontWeight={600}
            label="View Upload Progress"
          />
        </Box>
      )}
      {!csvError && !youtubeSuccess && (
        <Box
          style={{
            maxHeight: 400,
            overflowY: 'auto',
            background: isDarkMode ? page.tableBg : page.altBg,
          }}
          margin={{ top: '1.5rem' }}
          round="var(--app-radius)"
          pad="1rem"
          gap="0.5rem"
        >
          {allFiles.length === 0 && (
            <Text size="0.9rem" color={page.hintText}>
              Choose Asset Files to get started
            </Text>
          )}
          {allFiles.map((file, i) => (
            <Box
              key={file.name}
              direction="row"
              gap="0.5rem"
              width="100%"
              height={{ min: 'fit-content' }}
              align="center"
              justify="between"
              background={isDarkMode ? page.tableBg : page.altBg}
              border={(i !== allFiles.length - 1) && {
                color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
              pad={{ bottom: (i !== allFiles.length - 1) && '0.5rem' }}
            >
              <Box direction="column" gap="0.5rem">
                <Text size="0.8rem" color={page.tableText}>{file.name}</Text>
                <Box direction="row" align="center" gap="0.5rem">
                  <Meter
                    values={[{ value: progress[file.name] || 0, color: page.highlight }]}
                    thickness="small"
                    size="small"
                    background={page.inputBg}
                    max={100}
                  />
                  <Text size="0.8rem" color={page.tableText}>
                    {progress[file.name] || 0}
                    %
                  </Text>
                  <Text size="0.85rem" color="var(--app-success)">
                    {status[file.name]}
                  </Text>
                </Box>
              </Box>
              {/* Only show remove button before upload */}
              {!uploading && !filesUploaded && (
                <StyledFormInputClear
                  plain
                  icon={<FormClose size="1.5rem" color={page.tableText} />}
                  onClick={() => {
                    if (file === csvFile) {
                      setCsvFile(null);
                      setCsvError('');
                    } else {
                      setFiles((prev) => prev.filter((f) => f.name !== file.name));
                      setStatus((prev) => {
                        const newStatus = { ...prev };
                        delete newStatus[file.name];
                        return newStatus;
                      });
                      setProgress((prev) => {
                        const newProgress = { ...prev };
                        delete newProgress[file.name];
                        return newProgress;
                      });
                    }
                  }}
                  style={{
                    cursor: 'pointer', position: 'relative', padding: '0', top: '2px',
                  }}
                />
              )}
              {status[file.name] === 'Done' && (
                <StatusGood size="1.35rem" color="var(--app-success)" />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

BulkAssetUploader.propTypes = {
  csvHeaders: PropTypes.arrayOf(PropTypes.object).isRequired,
  uploadBulkAssets: PropTypes.func.isRequired,
  onS3UploadStart: PropTypes.func.isRequired,
  assetLabel: PropTypes.string,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  withCsv: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
  isRedflagUpload: PropTypes.bool,
  hasAllowlistedChannels: PropTypes.bool,
};

export default BulkAssetUploader;
